/**
 * Insieme chiuso delle icone di B.Site (research R-18).
 *
 * Icone lineari su griglia 24 px, tratto 1,75 px, estremità arrotondate. I tracciati vengono
 * dalle tavole `docs/design/moderna/` e, dove mancano, da Lucide (https://lucide.dev, licenza
 * ISC, Copyright (c) Lucide Contributors). Non si aggiunge alcuna dipendenza npm: un'icona nuova
 * si aggiunge qui, con la sua fonte.
 */

export type IconShape =
  | { readonly type: 'path'; readonly d: string }
  | { readonly type: 'circle'; readonly cx: number; readonly cy: number; readonly r: number }
  | {
      readonly type: 'rect';
      readonly x: number;
      readonly y: number;
      readonly width: number;
      readonly height: number;
      readonly rx: number;
    };

const path = (d: string): IconShape => ({ type: 'path', d });
const circle = (cx: number, cy: number, r: number): IconShape => ({ type: 'circle', cx, cy, r });
const rect = (x: number, y: number, width: number, height: number, rx: number): IconShape => ({
  type: 'rect',
  x,
  y,
  width,
  height,
  rx,
});

export const ICONS = {
  // Dalle tavole "App moderna".
  home: [path('M3 11l9-7 9 7'), path('M5 10v10h14V10')],
  activities: [path('M9 6h11M9 12h11M9 18h11'), path('M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2')],
  document: [
    path('M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z'),
    path('M14 3v5h5M9 13h6M9 17h6'),
  ],
  participants: [
    circle(9, 8, 3.5),
    path('M2.5 20a6.5 6.5 0 0 1 13 0'),
    path('M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6 6 0 0 1 3.5 6'),
  ],
  plus: [path('M12 5v14M5 12h14')],
  search: [circle(11, 11, 6.5), path('M20 20l-4.2-4.2')],
  bell: [
    path('M6 9a6 6 0 0 1 12 0c0 6 2.5 8 2.5 8h-17S6 15 6 9'),
    path('M10 20.5a2.2 2.2 0 0 0 4 0'),
  ],
  'chevron-right': [path('M9 6l6 6-6 6')],
  'chevron-left': [path('M15 6l-6 6 6 6')],
  calendar: [rect(3.5, 5, 17, 15.5, 3), path('M3.5 10h17M8 3v4M16 3v4')],
  clock: [circle(12, 12, 8.5), path('M12 7.5V12l3 2')],
  warning: [path('M12 3.5l9.5 16.5h-19z'), path('M12 10v4.5M12 17.5v.01')],
  lock: [rect(5, 10.5, 14, 10, 2.5), path('M8 10.5V8a4 4 0 0 1 8 0v2.5')],
  sidebar: [rect(3.5, 4.5, 17, 15, 3), path('M9.5 4.5v15')],
  more: [circle(5, 12, 1.3), circle(12, 12, 1.3), circle(19, 12, 1.3)],
  cast: [path('M12 3l8 4.5v9L12 21l-8-4.5v-9z'), path('M12 12l8-4.5M12 12v9M12 12L4 7.5')],
  filter: [path('M4 6h16M7 12h10M10 18h4')],

  // Da Lucide (ISC).
  menu: [path('M4 6h16M4 12h16M4 18h16')],
  close: [path('M18 6 6 18'), path('m6 6 12 12')],
  eye: [
    path(
      'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    ),
    circle(12, 12, 3),
  ],
  'eye-off': [
    path(
      'M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49',
    ),
    path('M14.084 14.158a3 3 0 0 1-4.242-4.242'),
    path(
      'M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143',
    ),
    path('m2 2 20 20'),
  ],
  user: [path('M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'), circle(12, 7, 4)],
  subscription: [rect(2, 5, 20, 14, 2), path('M2 10h20')],
  logout: [
    path('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'),
    path('m16 17 5-5-5-5'),
    path('M21 12H9'),
  ],
  sun: [
    circle(12, 12, 4),
    path('M12 2v2M12 20v2m-7.07-17.07 1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2'),
    path('m6.34 17.66-1.41 1.41M19.07 4.93l-1.41 1.41'),
  ],
  moon: [path('M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z')],
  monitor: [rect(2, 3, 20, 14, 2), path('M8 21h8M12 17v4')],
  'map-pin': [
    path(
      'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
    ),
    circle(12, 10, 3),
  ],
  building: [
    path('M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z'),
    path('M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2'),
    path('M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2'),
    path('M10 6h4M10 10h4M10 14h4M10 18h4'),
  ],
  site: [
    path('M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5'),
    path('M14 6a6 6 0 0 1 6 6v3'),
    path('M4 15v-3a6 6 0 0 1 6-6'),
    rect(2, 15, 20, 4, 1),
  ],
  check: [path('M20 6 9 17l-5-5')],
  info: [circle(12, 12, 10), path('M12 16v-4M12 8h.01')],
  'alert-circle': [circle(12, 12, 10), path('M12 8v4M12 16h.01')],
  'check-circle': [circle(12, 12, 10), path('m9 12 2 2 4-4')],
  'chevron-down': [path('m6 9 6 6 6-6')],
  grid: [rect(3, 3, 7, 7, 1), rect(14, 3, 7, 7, 1), rect(14, 14, 7, 7, 1), rect(3, 14, 7, 7, 1)],
  'arrow-left': [path('m12 19-7-7 7-7'), path('M19 12H5')],
  refresh: [path('M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8'), path('M21 3v5h-5')],
  mail: [rect(2, 4, 20, 16, 2), path('m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7')],
  hourglass: [
    path('M5 22h14M5 2h14'),
    path('M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22'),
    path('M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2'),
  ],
  camera: [
    path(
      'M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z',
    ),
    circle(12, 13, 3),
  ],
  image: [
    rect(3, 3, 18, 18, 2),
    circle(9, 9, 2),
    path('m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'),
  ],
  upload: [
    path('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'),
    path('m17 8-5-5-5 5'),
    path('M12 3v12'),
  ],
  // "key-round" di Lucide: le passkey.
  key: [
    path(
      'M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z',
    ),
    circle(16.5, 7.5, 0.5),
  ],
  shield: [
    path(
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    ),
  ],
  'shield-check': [
    path(
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    ),
    path('m9 12 2 2 4-4'),
  ],
  fingerprint: [
    path('M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4'),
    path('M14 13.12c0 2.38 0 6.38-1 8.88'),
    path('M17.29 21.02c.12-.6.43-2.3.5-3.02'),
    path('M2 12a10 10 0 0 1 18-6'),
    path('M2 16h.01'),
    path('M21.8 16c.2-2 .131-5.354 0-6'),
    path('M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2'),
    path('M8.65 22c.21-.66.45-1.32.57-2'),
    path('M9 6.8a6 6 0 0 1 9 5.2v2'),
  ],
  laptop: [
    path(
      'M18 5a2 2 0 0 1 2 2v8.526a2 2 0 0 0 .212.897l1.068 2.127a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45l1.068-2.127A2 2 0 0 0 4 15.526V7a2 2 0 0 1 2-2z',
    ),
    path('M20.054 15.987H3.946'),
  ],
  smartphone: [rect(5, 2, 14, 20, 2), path('M12 18h.01')],
  // "rotate-ccw-square" e "rotate-cw-square" di Lucide: rotazione di 90° della foto.
  'rotate-left': [
    path('M20 9V7a2 2 0 0 0-2-2h-6'),
    path('m15 2-3 3 3 3'),
    path('M20 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2'),
  ],
  'rotate-right': [
    path('M12 5H6a2 2 0 0 0-2 2v3'),
    path('m9 8 3-3-3-3'),
    path('M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2'),
  ],
  // "crosshair" di Lucide: centrare.
  center: [circle(12, 12, 10), path('M22 12h-4M6 12H2M12 6V2M12 22v-4')],
  // "rotate-ccw" di Lucide: ripristinare.
  reset: [path('M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'), path('M3 3v5h5')],
  'zoom-in': [circle(11, 11, 8), path('m21 21-4.3-4.3'), path('M11 8v6M8 11h6')],
  'zoom-out': [circle(11, 11, 8), path('m21 21-4.3-4.3'), path('M8 11h6')],
  copy: [rect(8, 8, 14, 14, 2), path('M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2')],
  download: [
    path('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'),
    path('m7 10 5 5 5-5'),
    path('M12 15V3'),
  ],
  printer: [
    path('M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2'),
    path('M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6'),
    rect(6, 14, 12, 8, 1),
  ],
  circle: [circle(12, 12, 9)],
  briefcase: [path('M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16'), rect(2, 6, 20, 14, 2)],
  history: [
    path('M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'),
    path('M3 3v5h5'),
    path('M12 7v5l4 2'),
  ],
  palette: [
    path(
      'M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z',
    ),
    circle(13.5, 6.5, 0.5),
    circle(17.5, 10.5, 0.5),
    circle(6.5, 12.5, 0.5),
    circle(8.5, 7.5, 0.5),
  ],
  trash: [
    path('M3 6h18'),
    path('M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6'),
    path('M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2'),
  ],
} as const satisfies Record<string, readonly IconShape[]>;

export type IconName = keyof typeof ICONS;

export const ICON_NAMES = Object.keys(ICONS) as IconName[];
